import { Link } from 'react-router-dom';
import { useWaitlist } from './MarketingLayout';
import { useMarketingMeta } from './useMarketingMeta';

export default function OrganisationsPage() {
  const { openWaitlist } = useWaitlist();

  useMarketingMeta({
    title: 'VolunteerCo · For Organisations',
    description:
      'VolunteerCo helps clubs and community groups set up clear ongoing volunteer roles with hours that can be tracked and signed off. Australia-wide.',
  });

  return (
    <>
      <section className="oct16-page-hero" aria-labelledby="orgs-page-title">
        <div className="oct16-container oct16-page-hero-grid">
          <div className="oct16-page-hero-inner">
            <p className="oct16-eyebrow" data-reveal>
              For Organisations
            </p>
            <h1 id="orgs-page-title" className="oct16-page-title" data-reveal>
              Roles that run.
              <span className="oct16-blue-inline"> Volunteers who return.</span>
            </h1>
            <p className="oct16-body" data-reveal>
              VolunteerCo is building a clearer way for clubs and community groups to set up ongoing roles that people
              can commit to.
            </p>
            <div className="oct16-hero-actions" data-reveal>
              <button type="button" className="oct16-primary" onClick={() => openWaitlist({ persona: 'club-charity' })}>
                Join the waitlist
              </button>
              <Link className="oct16-secondary" to={{ pathname: '/', hash: '#impact' }}>
                See how it works
              </Link>
            </div>
          </div>

          <div className="oct16-page-hero-media" aria-hidden="true">
            <picture className="oct16-photo oct16-photo-wide">
              <source srcSet="/landing/oct16/organisations-page-800.webp 800w, /landing/oct16/organisations-page-1600.webp 1600w, /landing/oct16/organisations-page-2400.webp 2400w" />
              <img
                src="/landing/oct16/organisations-page-1600.webp"
                alt=""
                loading="lazy"
                decoding="async"
                sizes="(max-width: 960px) 92vw, 44vw"
              />
            </picture>
          </div>
        </div>
      </section>

      <section className="oct16-page-section" aria-labelledby="orgs-how-title">
        <div className="oct16-container">
          <p className="oct16-label" data-reveal>
            How it works
          </p>
          <h2 id="orgs-how-title" className="oct16-section-title" data-reveal>
            What we do with you
          </h2>

          <div className="oct16-steps" role="list" aria-label="How VolunteerCo works for organisations">
            <article className="oct16-step" role="listitem" data-reveal>
              <div className="oct16-step-num" aria-hidden="true">
                01
              </div>
              <div className="oct16-step-copy">
                <h3>Define a clear role.</h3>
                <p>We shape an ongoing role with clear tasks, timing, and what “done” looks like.</p>
              </div>
            </article>
            <article className="oct16-step" role="listitem" data-reveal>
              <div className="oct16-step-num" aria-hidden="true">
                02
              </div>
              <div className="oct16-step-copy">
                <h3>Make it easy to say yes.</h3>
                <p>Volunteers see what they’re signing up for at a glance, and what the weekly commitment is.</p>
              </div>
            </article>
            <article className="oct16-step" role="listitem" data-reveal>
              <div className="oct16-step-num" aria-hidden="true">
                03
              </div>
              <div className="oct16-step-copy">
                <h3>Track and sign off hours.</h3>
                <p>Weekly hours can be tracked and signed off by the club or community group.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="oct16-page-section" aria-labelledby="orgs-need-title">
        <div className="oct16-container">
          <p className="oct16-label" data-reveal>
            What we need from your club
          </p>
          <h2 id="orgs-need-title" className="oct16-section-title" data-reveal>
            A few basics that make it work
          </h2>

          <ul className="oct16-points" aria-label="What VolunteerCo needs from clubs" data-reveal>
            <li className="oct16-point">
              A clear role and the regular time it happens.
            </li>
            <li className="oct16-point">
              A point of contact on the day so volunteers can check in.
            </li>
            <li className="oct16-point">
              Signing off weekly hours.
            </li>
            <li className="oct16-point">
              A welcoming first session so people feel comfortable coming back.
            </li>
          </ul>
        </div>
      </section>

      <section className="oct16-page-section" aria-labelledby="orgs-faq-title">
        <div className="oct16-container">
          <p className="oct16-label" data-reveal>
            FAQs
          </p>
          <h2 id="orgs-faq-title" className="oct16-section-title" data-reveal>
            A few quick answers
          </h2>
          <div className="oct16-faq" data-reveal>
            <details>
              <summary>Is it free for clubs?</summary>
              <p>Yes. VolunteerCo is free for clubs and community groups in the early pilot.</p>
            </details>
            <details>
              <summary>What kinds of roles work best?</summary>
              <p>Roles that happen weekly and are easy to repeat: canteen, scoring, line marking, setup and pack-down.</p>
            </details>
          </div>
          <div className="oct16-page-cta" data-reveal>
            <button type="button" className="oct16-primary" onClick={() => openWaitlist({ persona: 'club-charity' })}>
              Join the waitlist
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

